'use strict'

import React from 'react';
import { useEffect, useState, useRef, useMemo } from 'react';

import { Button, Form, Modal, Row, Col, Table, OverlayTrigger, Tooltip, Popover } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import useRequest from '../helper/useRequest';
import Loader from '../helper/Loader';
import Alert from '../helper/Alert';

import useConfirmation from '../helper/Confirmation';
import Select from 'react-select';

import TableHeader from '../helper/TableHeader';
import { customFilterFunction } from '../helper/CustomFilter';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    useReactTable,
} from '@tanstack/react-table';

import { formatDate } from '../helper/DateTimeFormater';

import { code } from '../helper/Schema';

const columnHelper = createColumnHelper();


function CompanyModal({ modal, setModal, organisations, company, addCompany, updateCompany }) {
    const [message, setMessage] = useState("");
    const [organisation, setOrganisation] = useState(0);

    const formRef = useRef(null);

    const schema = {
        Code: code,
    };

    const closeModal = () => {
        setMessage("");
        setModal(false);
    }

    const onSubmit = (values) => {
        values = { ...values, OrganisationID: organisation.value, OrganisationName: organisation.label };
        if (company) {
            updateCompany(values);
        } else {
            addCompany(values);
        }
    }

    const options = useMemo(() => {
        return organisations.map((orga) => { return { value: orga.OrganisationID, label: orga.OrganisationName } })
    }, [organisations]);

    useEffect(() => {
        if (!formRef.current) return;
        if (!company) {
            formRef.current.setFieldValue("OrganisationCouponID", "");
            formRef.current.setFieldValue("OrganisationID", "");
            formRef.current.setFieldValue("Activated", "");
            formRef.current.setFieldValue("Expired", "");
            setOrganisation(0);
        } else {
            formRef.current.setFieldValue("OrganisationCouponID", company.OrganisationCouponID);
            
            formRef.current.setFieldValue("Activated", company.Activated);
            formRef.current.setFieldValue("Expired", company.Expired);

            const foundOrga = organisations.find(orga => orga.OrganisationID == company.OrganisationID);
            if (!foundOrga) return;
            setOrganisation({ value: foundOrga.OrganisationID, label: foundOrga.OrganisationName });
        }
    }, [company]);

    return (
        <Modal size="lg" show={modal} onHide={closeModal}>
            <Modal.Header closeButton>
                <Modal.Title>Organisation verwalten</Modal.Title>
            </Modal.Header>
            <Formik
                innerRef={(f) => formRef.current = f}
                onSubmit={(values) => {
                    onSubmit(values);
                }}
                initialValues={{
                    OrganisationCouponID: "",
                    OrganisationID: "",
                    Activated: "",
                    Expired: "",
                }}
            >
                {
                    ({
                        handleSubmit,
                        handleChange,
                        handleBlur,
                        values,
                        touched,
                        isValid,
                        errors,
                    }) => {
                        return (
                            <>
                                <Modal.Body>
                                    {message && <Alert type="error" message={message} />}
                                    <Form noValidate onSubmit={handleSubmit}>
                                        <Form.Group as={Row} className="mb-3" controlId="Notes">
                                            <Form.Label>Zugeordnete Organisation:</Form.Label>
                                            <Select id="organisation" options={options} value={organisation} isDisabled={company} onChange={(option) => setOrganisation(option)} />
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="Activated">
                                            <Form.Label column sm={6}>Gültig ab dem:</Form.Label>
                                            <Col>
                                                <Form.Control type="date" value={values.Activated} onChange={handleChange} />
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="Expired">
                                            <Form.Label column sm={6}>Gültig bis zum:</Form.Label>
                                            <Col>
                                                <Form.Control type="date" value={values.Expired} onChange={handleChange} />
                                            </Col>
                                        </Form.Group>
                                    </Form>
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button type="button" variant="secondary" onClick={closeModal}>
                                        Abbrechen
                                    </Button>
                                    <Button type="submit" variant="primary" onClick={handleSubmit}>
                                        {!company && "Erstellen"}
                                        {company && "Speichern"}
                                    </Button>
                                </Modal.Footer>
                            </>
                        );
                    }
                }
            </Formik>
        </Modal>
    );
}

function Coupon() {
    const context = useConfirmation();
    const { request } = useRequest();

    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);

    const [id, setId] = useState(0);
    const [coupon, setCoupon] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(false);
    const [globalCompanies, setGlobalCompanies] = useState([]);

    const [modalCreate, setModalCreate] = useState(false);

    const fetchData = async () => {
        let path = location.pathname.split("/");
        if (path.length < 4) return;

        let id = path[3];
        setId(id);

        try {
            let result = await request("/Operator/GetCoupon/" + id);
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten.");
                }
            }
            else {
                setCoupon(json.coupon);
                setCompanies(json.companies);
                setGlobalCompanies(json.globalCompanies);
            }
        }
        catch (ex) {
            console.error(ex);
            toast.error("Es ist ein Fehler aufgetreten.");
        }
    };


    const addCompany = async (values) => {
        try {
            let result = await request("/Operator/AddCouponOrganisation", { ...values, CouponID: coupon.CouponID });
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Etwas ist schief gelaufen.");
                }
            }
            else {
                setCompanies(oldCompanies => {
                    return [...oldCompanies, { OrganisationCouponID: json.OrganisationCouponID, CouponID: coupon.CouponID, Activated: values.Activated, Expired: values.Expired, OrganisationInfo: { OrganisationName: values.OrganisationName } }];
                });
                toast.success("Gutschein wurde erfolgreich aktiviert.");
                setModalCreate(false);
            }
        }
        catch (ex) {
            toast.error(ex.message);
        }
    }

    const updateCompany = async (values) => {
        try {
            let result = await request("/Operator/UpdateCouponOrganisation", { ...values });
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Etwas ist schief gelaufen.");
                }
            }
            else {
                setCompanies(oldCompanies => 
                    oldCompanies.map(c => {
                        if (c.OrganisationCouponID == values.OrganisationCouponID) {
                            return { ...c, ...values };
                        }

                        return c;
                    })
                );
                toast.success("Zuordnung wurde erfolgreich aktualisiert.");
                setModalCreate(false);
                setSelectedCompany(false);
            }
        }
        catch (ex) {
            toast.error(ex.message);
        }
    }

    const deleteCompany = async (company) => {
        try {
            let result = await request("/Operator/DeleteCouponOrganisation", { OrganisationCouponID: company.OrganisationCouponID });
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Etwas ist schief gelaufen.");
                }
            }
            else {
                setCompanies(oldCompanies =>
                    oldCompanies.filter(c => {
                        if (c.OrganisationCouponID == company.OrganisationCouponID) {
                            return false;
                        }

                        return true;
                    })
                );
                toast.success("Zuordnung wurde erfolgreich aktualisiert.");
                setModalCreate(false);
                setSelectedCompany(false);
            }
        }
        catch (ex) {
            toast.error(ex.message);
        }
    }

    useEffect(() => {  
        fetchData();
    }, []);


    const columns = [
        columnHelper.accessor('StatusString', {
            minSize: 50,
            header: () => {
                return (
                    <OverlayTrigger popperConfig={{
                        modifiers: {
                            name: "offset",
                            options: {
                                offset: [0, 20]
                            }
                        }
                    }} placement="left" overlay={(
                        <Popover id="business-status-info">
                            <Popover.Header as="h3">Aktiviert</Popover.Header>
                            <Popover.Body>
                                <div><span className="bookedCircle me-2"></span>nein</div>
                                <div><span className="bookedCircle booked me-2"></span>ja</div>
                            </Popover.Body>
                        </Popover>
                    )}>
                        <span>Aktiviert</span>
                    </OverlayTrigger>
                );
            },
            cell: info => {
                let statusCircle = <span className="bookedCircle"></span>;
                if (info.getValue() == "ja") {
                    statusCircle = <span className="bookedCircle booked"></span>;
                }
                return (
                    <OverlayTrigger placement="left" overlay={<Tooltip>{info.getValue()}</Tooltip>}>
                        {statusCircle}
                    </OverlayTrigger>
                );
            },
            enableColumnFilter: true,
            filterFn: customFilterFunction,
            meta: {
                filterComponent: "select",
            }
        }),
        columnHelper.accessor('OrganisationInfo.OrganisationName', {
            header: () => <span>Organisation</span>,
            cell: info => <a href={"/Operator/Business/" + info.row.original.OrganisationID}>{info.getValue()}</a>,
            enableColumnFilter: true
        }),
        columnHelper.accessor('Activated', {
            header: () => <span>Gültig ab</span>,
            cell: info => info.getValue() ? formatDate(info.getValue()) : "",
            enableColumnFilter: true
        }),
        columnHelper.accessor('Expired', {
            header: () => <span>Gültig bis</span>,
            cell: info => info.getValue() ? formatDate(info.getValue()) : "",
            enableColumnFilter: true
        }),
        columnHelper.accessor('OrganisationCouponID', {
            header: () => "",
            cell: info => {
                return <div className="text-end">
                    <OverlayTrigger overlay={<Tooltip>Zuordnung bearbeiten</Tooltip>}>
                        <Button size="sm" variant="primary" className="me-2" onClick={() => { setSelectedCompany(info.row.original); setModalCreate(true); }}><i className="fa-solid fa-fw fa-pen"></i></Button>
                    </OverlayTrigger>
                    <OverlayTrigger overlay={<Tooltip>Zuordnung löschen</Tooltip>}>
                        <Button size="sm" variant="danger" onClick={() => context.openModal("Wirklich löschen?", "Möchten Sie die Organisation wirklich von diesem Coupon entfernen?", () => deleteCompany(info.row.original))}><i className="fa-solid fa-fw fa-trash"></i></Button>
                    </OverlayTrigger>
                </div>;
            },
            enableColumnFilter: false,
            enableSorting: false
        })
    ];

    const reactTable = useReactTable({
        data: companies || [],
        columns,
        state: {
            sorting,
            columnFilters,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,

        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    if (coupon === false) return <Loader />

    return (
        <>
            <Row>
                <Col xs="12" md="8">
                    <Table striped>
                        <thead>
                            {reactTable.getHeaderGroups().map(headerGroup => (
                                <TableHeader key={headerGroup.id} headerGroup={headerGroup} flexRender={flexRender} table={reactTable} />
                            ))}
                        </thead>
                        <tbody>
                            {reactTable.getRowModel().rows.map(row => (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map(cell => {
                                        return (
                                            <td key={cell.id}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        )
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    <Button onClick={() => { setSelectedCompany(false); setModalCreate(true) }}>Organisation hinzufügen</Button>
                </Col>
                <Col xs="12" md="4">
                 
                    <Form.Label>Gutscheininformationen</Form.Label>
                    <Table>
                        <tbody>
                            <tr>
                                <td>Code: </td>
                                <td>{coupon.Code}</td>
                            </tr>
                            <tr>
                                <td>Maximale Anwendungen: </td>
                                <td>{coupon.MaxBookings}</td>
                            </tr>
                            <tr>
                                <td>Discount: </td>
                                <td>{coupon.DiscountPercentage} %</td>
                            </tr>
                            <tr>
                                <td>Notizen: </td>
                                <td>{coupon.Notes}</td>
                            </tr>
                            <tr>
                                <td>Gültig ab: </td>
                                <td>{formatDate(coupon.Activated)}</td>
                            </tr>
                            <tr>
                                <td>Gültig bis: </td>
                                <td>{formatDate(coupon.Expired)}</td>
                            </tr>
                        </tbody>
                    </Table>
                </Col>
            </Row>
            <CompanyModal modal={modalCreate} setModal={setModalCreate} organisations={globalCompanies} company={selectedCompany} addCompany={addCompany} updateCompany={updateCompany} />
        </>
    );
}

export default Coupon;